/**
 * Registry for predesigned Life Skills GO cards
 * Defines available predesigned card types with their configurations
 */

// export interface PredesignedCardDefinition {
//   id; // e.g., 'lsgo-emotional-wellbeing-metric'
//   name; // Display name
//   description; // Tooltip/help text
//   icon; // Emoji or icon identifier
//   baseType: WidgetType; // 'metric' | 'line-chart' | 'bar-chart' | 'table'
//   defaultConfig; // Initial configuration
//   allowedCustomizations: {
//     title?;
//     filters?;
//     layout?[]; // Which layout options are allowed
//     dataSources?: 'fixed' | 'limited' | 'full'; // Restriction level
//   };
//   customDrillDownHandler?; // Reference to handler function
//   customSummaryExtractor?; // Reference to extractor function
// }

/**
 * Registry of all available predesigned cards
 * Add new predesigned cards here
 */
export const PREDESIGNED_CARD_REGISTRY: Record<string, PredesignedCardDefinition> = {
    'lsgo-report-summary': {
        id: 'lsgo-report-summary',
        name: 'Report Summary',
        description: 'Text summary with handlebars variables for dynamic content',
        icon: '📝',
        baseType: 'text',
        defaultConfig: {
            title: 'Report Summary',
            type: 'text',
            dataSources: [],
            config: {
                template: `<p>In {{year}}, <strong>{{schoolName}}</strong> utilised Life Skills GO as a planned approach to wellbeing that empowers student voice and supports students to connect, succeed, thrive and learn.</p>

<p><strong>{{studentCheckInPercentage}}%</strong> of students utilised the emotion check-in throughout the year. Of these check-ins, <strong>{{averageReadyToLearn}}%</strong> on average each day indicated they were ready to learn.</p>`,
                variables: {
                    studentCheckInPercentage: 100,
                    averageReadyToLearn: 78,
                },
            },
        },
        allowedCustomizations: {
            title: true,
            filters: false,
            layout: ['titleAlignment', 'titleSize', 'contentSpacing'],
            dataSources: 'fixed',
        },
    },
    'lsgo-context-comparison-chart': {
        id: 'lsgo-context-comparison-chart',
        name: 'Context Comparison',
        description: 'Compare emotional states across different contexts',
        icon: '📊',
        baseType: 'bar-chart',
        defaultConfig: {
            title: 'Context Comparison',
            type: 'bar-chart',
            dataSources: [
                {
                    id: 'ds-context-comparison-rtl',
                    sourceId: 'ready-to-learn',
                    label: 'Ready to Learn',
                    enabled: true,
                    color: '#14B8A6', // Teal
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-context-comparison-processing',
                    sourceId: 'processing',
                    label: 'In a state of processing',
                    enabled: true,
                    color: '#EAB308', // Mustard
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-context-comparison-not-ready',
                    sourceId: 'not-ready',
                    label: 'Not Ready to Learn',
                    enabled: true,
                    color: '#2196F3', // Blue
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-context-comparison-heightened',
                    sourceId: 'heightened-emotion',
                    label: 'Heightened State of Emotion',
                    enabled: true,
                    color: '#9C27B0', // Purple
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-context-comparison-overload',
                    sourceId: 'emotional-overload',
                    label: 'Emotional Overload',
                    enabled: true,
                    color: '#F44336', // Red
                    filters: { period: 'week' },
                },
            ],
            config: {
                chartType: 'bar',
                timeAxis: 'week',
                showLegend: true,
                showGrid: true,
                format: 'count',
                barDisplayMode: 'stacked',
                groupBy: 'context',
                dateRange: { period: 'week' },
            },
            drillDown: {
                enabled: true,
                defaultMode: 'aggregated',
                aggregationLevels: ['aggregated', 'by-context', 'by-class', 'by-date'],
                maxRawRecords: 1000,
                showRawOption: true,
            },
        },
        allowedCustomizations: {
            title: true,
            filters: true,
            layout: ['titleAlignment', 'legendPosition'],
            dataSources: 'limited',
        },
        customDrillDownHandler: 'context-comparison',
        customSummaryExtractor: 'context-comparison',
    },
    'lsgo-state-of-emotion-comparison': {
        id: 'lsgo-state-of-emotion-comparison',
        name: 'State of Emotion Comparison',
        description: 'Compare the distribution of emotional states as percentages',
        icon: '🥧',
        baseType: 'bar-chart', // Using bar-chart as base type since pie isn't a separate widget type
        defaultConfig: {
            title: 'State of Emotion Comparison',
            type: 'bar-chart',
            dataSources: [
                {
                    id: 'ds-emotion-comparison-rtl',
                    sourceId: 'ready-to-learn',
                    label: 'Ready to Learn',
                    enabled: true,
                    color: '#14B8A6', // Teal
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-emotion-comparison-processing',
                    sourceId: 'processing',
                    label: 'In a state of processing',
                    enabled: true,
                    color: '#EAB308', // Mustard
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-emotion-comparison-not-ready',
                    sourceId: 'not-ready',
                    label: 'Not Ready to Learn',
                    enabled: true,
                    color: '#2196F3', // Blue
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-emotion-comparison-heightened',
                    sourceId: 'heightened-emotion',
                    label: 'Heightened State of Emotion',
                    enabled: true,
                    color: '#9C27B0', // Purple
                    filters: { period: 'week' },
                },
                {
                    id: 'ds-emotion-comparison-overload',
                    sourceId: 'emotional-overload',
                    label: 'Emotional Overload',
                    enabled: true,
                    color: '#F44336', // Red
                    filters: { period: 'week' },
                },
            ],
            config: {
                chartType: 'pie',
                showLegend: true,
                showGrid: false,
                format: 'percentage',
                dateRange: { period: 'week' },
            },
            drillDown: {
                enabled: true,
                defaultMode: 'aggregated',
                aggregationLevels: ['aggregated', 'by-context', 'by-class', 'by-date'],
                maxRawRecords: 1000,
                showRawOption: true,
            },
        },
        allowedCustomizations: {
            title: true,
            filters: true,
            layout: ['titleAlignment', 'legendPosition'],
            dataSources: 'limited',
        },
        customDrillDownHandler: 'emotion-comparison',
        customSummaryExtractor: 'emotion-comparison',
    },
    'lsgo-student-engagement-table': {
        id: 'lsgo-student-engagement-table',
        name: 'Student Engagement',
        description: 'View student engagement by year level across term windows',
        icon: '📋',
        baseType: 'table',
        defaultConfig: {
            title: 'Student Engagement',
            type: 'table',
            dataSources: [],
            config: {
                dataSource: 'checkin-summary',
                columnConfigs: [],
                dateRange: { period: 'year' },
                groupBy: ['grade'],
                joins: {
                    includeUsers: false,
                    includeClasses: false,
                    includeContext: false,
                },
                pagination: false,
                pageSize: 100,
                // Engagement metric configuration
                engagementMetric: {
                    entityType: 'student',
                    period: {
                        type: 'year',
                        year: 2025,
                    },
                    window: {
                        unit: 'term',
                        size: 1,
                        alignment: 'calendar_month',
                    },
                    metric: {
                        type: 'distinct_days',
                        minEventsPerDay: 1,
                        threshold: 3,
                    },
                    aggregation: {
                        type: 'fraction_of_windows_meeting_threshold',
                        includeEmptyWindows: false,
                    },
                },
                // Custom config for engagement table
                engagementTable: {
                    period: 'year',
                    window: 'term',
                    windowCount: 4,
                },
            },
            drillDown: {
                enabled: true,
                defaultMode: 'aggregated',
                aggregationLevels: ['aggregated', 'by-grade', 'by-term'],
                maxRawRecords: 1000,
                showRawOption: true,
            },
        },
        allowedCustomizations: {
            title: true,
            filters: true,
            layout: ['titleAlignment'],
            dataSources: 'fixed',
        },
        customDrillDownHandler: 'student-engagement',
        customSummaryExtractor: 'student-engagement',
    },
    'lsgo-class-comparison-bar': {
        id: 'lsgo-class-comparison-bar',
        name: 'Class Comparison',
        description: 'Compare emotional wellbeing across classes',
        icon: '🏫',
        baseType: 'bar-chart',
        defaultConfig: {
            title: 'Class Comparison',
            type: 'bar-chart',
            dataSources: [
                {
                    id: 'ds-class-comparison',
                    sourceId: 'ready-to-learn',
                    label: 'Ready to Learn',
                    enabled: true,
                    filters: { context: 'all', period: 'week' },
                },
            ],
            config: {
                chartType: 'bar',
                timeAxis: 'week',
                showLegend: true,
                showGrid: true,
                format: 'percentage',
                barDisplayMode: 'grouped',
                groupBy: 'class',
                dateRange: { period: 'week' },
            },
            drillDown: {
                enabled: true,
                defaultMode: 'aggregated',
                aggregationLevels: ['aggregated', 'by-class', 'by-user'],
                maxRawRecords: 1000,
                showRawOption: true,
            },
        },
        allowedCustomizations: {
            title: true,
            filters: true,
            layout: ['titleAlignment', 'legendPosition'],
            dataSources: 'limited',
        },
        customDrillDownHandler: 'class-comparison',
        customSummaryExtractor: 'class-comparison',
    },
    'lsgo-class-engagement-bar': {
        id: 'lsgo-class-engagement-bar',
        name: 'Class Engagement',
        description: 'View yearly average engagement percentage by class',
        icon: '📊',
        baseType: 'bar-chart',
        defaultConfig: {
            title: 'Class Engagement',
            type: 'bar-chart',
            dataSources: [
                {
                    sourceId: 'engagement-percentage',
                    label: 'Engagement %',
                    color: 'rgba(76, 175, 80, 0.6)',
                    enabled: true,
                },
            ],
            config: {
                chartType: 'bar',
                showLegend: false,
                showGrid: true,
                format: 'percentage',
                barDisplayMode: 'grouped',
                groupBy: 'class',
                engagementMetric: {
                    entityType: 'class',
                    period: {
                        type: 'year',
                        year: 2025,
                    },
                    window: {
                        unit: 'week',
                        size: 1,
                        alignment: 'iso_week',
                    },
                    metric: {
                        type: 'distinct_days',
                        minEventsPerDay: 1,
                        threshold: 3,
                    },
                    aggregation: {
                        type: 'fraction_of_windows_meeting_threshold',
                        includeEmptyWindows: false,
                    },
                },
            },
            drillDown: {
                enabled: true,
                defaultMode: 'aggregated',
                aggregationLevels: ['aggregated', 'by-class', 'by-user'],
                maxRawRecords: 1000,
                showRawOption: true,
            },
        },
        allowedCustomizations: {
            title: true,
            filters: false, // Engagement metrics use their own period config
            layout: ['titleAlignment', 'legendPosition'],
            dataSources: 'fixed',
        },
        customDrillDownHandler: 'class-engagement',
        customSummaryExtractor: 'class-engagement',
    },
    'lsgo-check-in-emotions-table': {
        id: 'lsgo-check-in-emotions-table',
        name: 'Check-in Emotions',
        description: 'View top emotions by count aggregated by term for the period',
        icon: '📊',
        baseType: 'table',
        defaultConfig: {
            title: 'Check-in Emotions',
            type: 'table',
            dataSources: [],
            config: {
                dateRange: { period: 'year', year: 2025 },
                checkInEmotions: {
                    period: 'year',
                    aggregationWindow: 'term',
                    aggregationType: 'count',
                    topN: 10,
                },
            },
            drillDown: {
                enabled: false,
            },
        },
        allowedCustomizations: {
            title: true,
            filters: true,
            layout: ['titleAlignment'],
            dataSources: 'fixed',
        },
    },
    'lsgo-insights-list': {
        id: 'lsgo-insights-list',
        name: 'Insights',
        description: 'List of insights from the report data',
        icon: '💡',
        baseType: 'insights-list',
        defaultConfig: {
            title: 'Insights',
            type: 'insights-list',
            dataSources: [],
            config: {
                dateRange: { period: 'year', year: new Date().getFullYear() },
            },
        },
        allowedCustomizations: {
            title: true,
            filters: false,
            layout: ['titleAlignment', 'titleSize', 'contentSpacing'],
            dataSources: 'fixed',
        },
    },
    'lsgo-readiness-trend': {
        id: 'lsgo-readiness-trend',
        name: 'States of Emotion - Weekly Trend',
        description: 'Line chart showing readiness to learn trends over weeks',
        icon: '📈',
        baseType: 'line-chart',
        defaultConfig: {
            title: 'States of Emotion - Weekly Trend',
            type: 'line-chart',
            dataSources: [
                {
                    id: 'ds-readiness-rtl',
                    sourceId: 'ready-to-learn',
                    label: 'Ready to Learn',
                    enabled: true,
                    color: '#14B8A6',
                    filters: {
                        type: 'week',
                        year: 2026,
                        interval: 1,
                        term: 1,
                    },
                },
                {
                    id: 'ds-readiness-processing',
                    sourceId: 'processing',
                    label: 'In a state of processing',
                    enabled: true,
                    color: '#EAB308',
                    filters: {
                        type: 'week',
                        year: 2026,
                        interval: 1,
                        term: 1,
                    },
                },
                {
                    id: 'ds-readiness-not-ready',
                    sourceId: 'not-ready',
                    label: 'Not Ready to Learn',
                    enabled: true,
                    color: '#2196F3',
                    filters: {
                        type: 'week',
                        year: 2026,
                        interval: 1,
                        term: 1,
                    },
                },
                {
                    id: 'ds-readiness-heightened',
                    sourceId: 'heightened-emotion',
                    label: 'Heightened State of Emotion',
                    enabled: true,
                    color: '#9C27B0',
                    filters: {
                        type: 'week',
                        year: 2026,
                        interval: 1,
                        term: 1,
                    },
                },
                {
                    id: 'ds-readiness-overload',
                    sourceId: 'emotional-overload',
                    label: 'Emotional Overload',
                    enabled: true,
                    color: '#F44336',
                    filters: {
                        type: 'week',
                        year: 2026,
                        interval: 1,
                        term: 1,
                    },
                },
            ],
            config: {
                chartType: 'line',
                timeAxis: 'week',
                showLegend: true,
                showGrid: true,
                format: 'percentage',
                dateRange: {
                    type: 'week',
                    year: 2026,
                    interval: 1,
                    term: 1,
                },
            },
        },
        allowedCustomizations: {
            title: true,
            filters: true,
            layout: ['titleAlignment', 'legendPosition'],
            dataSources: 'limited',
        },
    },
};

/**
 * Get a predesigned card definition by ID
 */
export function getPredesignedCardDefinition(id): PredesignedCardDefinition | undefined {
    return PREDESIGNED_CARD_REGISTRY[id];
}

/**
 * Get all predesigned card definitions
 */
export function getAllPredesignedCards(): PredesignedCardDefinition[] {
    return Object.values(PREDESIGNED_CARD_REGISTRY);
}

/**
 * Create a widget config from a predesigned card definition
 */
export function createWidgetFromPredesigned(predesignedId, widgetId?) {
    const definition = getPredesignedCardDefinition(predesignedId);
    if (!definition) {
        return null;
    }

    const id = widgetId || `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const widget = {
        id,
        type: definition.baseType,
        title: definition.defaultConfig.title || definition.name,
        dataSources: definition.defaultConfig.dataSources || [],
        config: definition.defaultConfig.config || {},
        isPredesigned: true,
        predesignedType: definition.id,
        ...(definition.defaultConfig.style && { style: definition.defaultConfig.style }),
        ...(definition.defaultConfig.layout && { layout: definition.defaultConfig.layout }),
        ...(definition.defaultConfig.drillDown && { drillDown: definition.defaultConfig.drillDown }),
    };

    return widget;
}
